import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/*
 * The About page's interactive sections.
 *
 * The page read like a book: eight steps, twelve capability cards, a table of
 * eleven rows and three roadmap columns, all of it text laid out at once. Each
 * of these keeps the same words and the same facts, but shows one thing at a
 * time and lets the visitor choose which, so the page is something to use
 * rather than something to scroll.
 *
 * Nothing here moves on its own under prefers-reduced-motion, and every
 * control is a real button with its state on it, so the keyboard and a screen
 * reader get the same page.
 */

function useReducedMotion(): boolean {
  const [r, setR] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return r;
}

/* ------------------------------------------------------------------ */
/* The collections workflow                                           */
/* ------------------------------------------------------------------ */

export interface FlowStep {
  n: number; label: string; body: string; live: boolean; detail: string;
}

/** What each step looks like, drawn small. The figures are an illustration. */
function StepPreview({ n }: { n: number }) {
  switch (n) {
    case 1:
      return (
        <div className="ax-pv" aria-hidden>
          {[['1 to 7 days', 34, 'a'], ['8 to 30 days', 58, 'b'], ['31 to 60 days', 82, 'c'], ['Over 60 days', 46, 'd']].map(([l, w, t]) => (
            <div key={String(l)} className="ax-pv-bar"><span>{l}</span>
              <i className={`t-${t}`} style={{ ['--w' as string]: `${w}%` }} /></div>
          ))}
        </div>
      );
    case 2:
      return (
        <div className="ax-pv" aria-hidden>
          <svg viewBox="0 0 220 80" className="ax-pv-spark">
            <path d="M6 66 L60 52 L116 40 L170 24 L214 8" />
            {[[6, 66], [60, 52], [116, 40], [170, 24], [214, 8]].map(([x, y]) => <circle key={x} cx={x} cy={y} r="3.5" />)}
          </svg>
          <div className="ax-pv-chips"><span>Owes more each month for three months</span><span>Paid late 4 times in six months</span></div>
        </div>
      );
    case 3:
      return (
        <ol className="ax-pv ax-pv-rank" aria-hidden>
          <li><b>1</b><span>Unit B4</span><em>74 days, rising, promise broken</em></li>
          <li><b>2</b><span>Unit A2</span><em>40 days, pays late</em></li>
          <li><b>3</b><span>Unit C11</span><em>9 days</em></li>
        </ol>
      );
    case 4:
      return (
        <div className="ax-pv" aria-hidden>
          <p className="ax-pv-msg">Hello, your rent for October is KES 42,500 and is 40 days late. Please pay by M-Pesa or tell us when you can. Thank you.</p>
        </div>
      );
    case 5:
      return <ApproveDemo />;
    case 6:
      return (
        <div className="ax-pv ax-pv-send" aria-hidden>
          <span className="sms">SMS</span><span className="line" /><span className="ok">Sent</span>
          <span className="mail">Email</span><span className="line" /><span className="ok">Sent</span>
        </div>
      );
    case 7:
      return (
        <ul className="ax-pv ax-pv-log" aria-hidden>
          <li><i className="g" />Unit B4, SMS<em>Delivered</em></li>
          <li><i className="g" />Unit A2, email<em>Delivered</em></li>
          <li><i className="r" />Unit C11, SMS<em>Failed, kept</em></li>
        </ul>
      );
    default:
      return (
        <div className="ax-pv ax-pv-loop" aria-hidden>
          <svg viewBox="0 0 120 80"><path d="M20 40a32 24 0 1 1 20 22" /><path d="M40 62l-10-3 7-8" /></svg>
          <p>Still unpaid after the date you set, it returns to the top of the work order.</p>
        </div>
      );
  }
}

/** A working toggle, so "you approve it" is something the visitor does. */
function ApproveDemo() {
  const [done, setDone] = useState(false);
  return (
    <div className="ax-pv">
      <ul className="ax-pv-checks">
        {['Unit B4', 'Unit A2', 'Unit C11'].map((u) => (
          <li key={u} className={done ? 'on' : ''}><i />{u}</li>
        ))}
      </ul>
      <button type="button" className="ax-btn" onClick={() => setDone((d) => !d)} aria-pressed={done}>
        {done ? 'Approved, ready to send' : 'Approve these messages'}
      </button>
    </div>
  );
}

export function WorkflowExplorer({ steps, imageSrc, videoSrc, posterSrc }: {
  steps: FlowStep[]; imageSrc?: string; videoSrc?: string; posterSrc?: string;
}) {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [vidOk, setVidOk] = useState(true);
  // The loop is several megabytes: fetch it only once the section is in view, and
  // never on a small screen or with Data Saver on.
  const [seen, setSeen] = useState(false);
  const heavyOk = typeof window !== 'undefined' && window.innerWidth >= 900
    && !(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  const hovering = useRef(false);

  // Start travelling the loop the first time it is on screen, unless the
  // visitor prefers reduced motion; the first press of anything stops it.
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el || !('IntersectionObserver' in window)) { setSeen(true); return; }
    if (reduced) { setSeen(true); return; }
    const io = new IntersectionObserver((es) => {
      if (es[0]?.isIntersecting) { setSeen(true); setPlaying(true); io.disconnect(); }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  useEffect(() => {
    if (!playing || reduced) return;
    const t = window.setInterval(() => {
      if (!hovering.current) setActive((a) => (a + 1) % steps.length);
    }, 3400);
    return () => window.clearInterval(t);
  }, [playing, reduced, steps.length]);

  const pick = (i: number) => { setPlaying(false); setActive(i); };
  const s = steps[active]!;
  const live = steps.filter((x) => x.live).length;

  return (
    <div className="ax-flow" ref={box}
      onMouseEnter={() => { hovering.current = true; }} onMouseLeave={() => { hovering.current = false; }}>
      {videoSrc && vidOk && !reduced && seen && heavyOk && (
        <video className="ax-flow-bg" src={videoSrc} poster={posterSrc} autoPlay muted loop playsInline
          preload="metadata" aria-hidden onError={() => setVidOk(false)} />
      )}
      <div className="ax-flow-inner">
        <div className="ax-track" role="tablist" aria-label="The eight steps from a missed payment to a resolved one">
          {steps.map((x, i) => (
            <button key={x.label} type="button" role="tab" aria-selected={i === active}
              className={`ax-node ${x.live ? 'live' : 'soon'} ${i === active ? 'on' : ''} ${i < active ? 'past' : ''}`}
              onClick={() => pick(i)}>
              <span className="ax-node-n">{x.n}</span>
              <span className="ax-node-l">{x.label}</span>
            </button>
          ))}
        </div>

        <div className="ax-detail" role="tabpanel" key={s.label}>
          <div className="ax-detail-text">
            <span className={`ax-pill ${s.live ? 'live' : 'soon'}`}>{s.live ? 'Live today' : 'Being built'}</span>
            <h3>{s.n}. {s.label}</h3>
            <p className="ax-detail-line">{s.body}</p>
            <p className="ax-detail-more">{s.detail}</p>
            <div className="ax-controls">
              <button type="button" className="ax-btn ghost" onClick={() => pick((active + steps.length - 1) % steps.length)}>Back</button>
              <button type="button" className="ax-btn ghost" onClick={() => pick((active + 1) % steps.length)}>Next step</button>
              {!reduced && (
                <button type="button" className="ax-btn ghost" aria-pressed={playing} onClick={() => setPlaying((p) => !p)}>
                  {playing ? 'Pause' : 'Play the loop'}
                </button>
              )}
            </div>
          </div>
          <div className="ax-detail-view">
            <StepPreview n={s.n} />
            <span className="ax-note">An illustration, not a real portfolio.</span>
          </div>
        </div>

        <p className="ax-legend">
          {live} of {steps.length} steps run today. {steps.length - live === 1 ? 'One is' : `${steps.length - live} are`} being built.
        </p>

        {imageSrc && imgOk && (
          <div className="ax-whole">
            <button type="button" className="ax-btn" aria-expanded={showImage} onClick={() => setShowImage((v) => !v)}>
              {showImage ? 'Hide the one-page picture' : 'See the whole workflow as one picture'}
            </button>
            {showImage && (
              <figure className="ax-fig">
                <img src={imageSrc} loading="lazy" onError={() => setImgOk(false)}
                  alt="The eight steps from a missed payment to a resolved one: Detect, Analyse, Recommend, Draft, Approve, Send, Monitor and Escalate, with Escalate marked as being built." />
                <figcaption><a href={imageSrc} download>Download the picture</a></figcaption>
              </figure>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The capability explorer                                            */
/* ------------------------------------------------------------------ */

export interface Capability {
  icon: string; ic: string; name: string; tag: string; body: string; points: string[]; group: string;
}

export function CapabilityExplorer({ items, groups, icons }: {
  items: Capability[]; groups: string[]; icons: Record<string, ReactNode>;
}) {
  const [group, setGroup] = useState('All');
  const shown = useMemo(() => items.filter((c) => group === 'All' || c.group === group), [items, group]);
  const [name, setName] = useState(items[0]!.name);
  const cur = shown.find((c) => c.name === name) ?? shown[0]!;

  return (
    <div className="ax-cap">
      <div className="ax-chips" role="group" aria-label="Filter the platform by area">
        {['All', ...groups].map((g) => (
          <button key={g} type="button" className={`ax-chip ${g === group ? 'on' : ''}`} aria-pressed={g === group}
            onClick={() => { setGroup(g); const first = items.find((c) => g === 'All' || c.group === g); if (first) setName(first.name); }}>
            {g}{g === 'All' ? ` (${items.length})` : ` (${items.filter((c) => c.group === g).length})`}
          </button>
        ))}
      </div>
      <div className="ax-cap-grid">
        <ul className="ax-cap-list" role="tablist" aria-orientation="vertical">
          {shown.map((c) => (
            <li key={c.name}>
              <button type="button" role="tab" aria-selected={c.name === cur.name}
                className={`ax-cap-item ${c.name === cur.name ? 'on' : ''}`} onClick={() => setName(c.name)}>
                <span className={`mk-card-icon ic-${c.ic}`} aria-hidden>{icons[c.icon]}</span>
                <span><b>{c.name}</b><small>{c.tag}</small></span>
              </button>
            </li>
          ))}
        </ul>
        <article className="ax-cap-detail" role="tabpanel" key={cur.name}>
          <div className={`mk-card-icon ic-${cur.ic} big`} aria-hidden>{icons[cur.icon]}</div>
          <h3>{cur.name}</h3>
          <p className="ax-cap-tag">{cur.tag}</p>
          <p>{cur.body}</p>
          <ul className="ax-cap-points">
            {cur.points.map((p, i) => <li key={p} style={{ ['--i' as string]: i }}><span>{p}</span></li>)}
          </ul>
        </article>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* What makes us different, shown on the product itself               */
/* ------------------------------------------------------------------ */

export interface Pillar {
  icon: string; ic: string; title: string; body: string; shot: string; shotLabel: string;
}

const PILLAR_MS = 7000;

/**
 * Nine claims used to sit as nine identical cards, which asked the visitor to
 * take every one on trust. Here each claim sits beside the screen that backs
 * it. The stage walks through them on its own while it is on screen, stops for
 * good the moment a visitor picks one, and never moves for anyone who has
 * asked their device for less motion.
 */
export function PillarStage({ items, icons }: { items: Pillar[]; icons: Record<string, ReactNode> }) {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [inView, setInView] = useState(false);
  const [held, setHeld] = useState(false);
  const [taken, setTaken] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const cur = items[i]!;
  const playing = inView && !held && !taken && !reduced;

  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setInView(!!e?.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => setI((n) => (n + 1) % items.length), PILLAR_MS);
    return () => window.clearTimeout(t);
  }, [playing, i, items.length]);

  // Fetch the next screen while the current one is being read.
  useEffect(() => {
    if (!inView) return;
    const next = items[(i + 1) % items.length];
    if (next) new Image().src = `/screens/${next.shot}.webp?v=2`;
  }, [inView, i, items]);

  const pick = (n: number) => { setTaken(true); setI(n); };
  const onKey = (e: React.KeyboardEvent, n: number) => {
    const to = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? (n + 1) % items.length
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? (n + items.length - 1) % items.length
      : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    pick(to);
    tabs.current[to]?.focus();
  };

  return (
    <div className="ax-pillars" ref={root}
      onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}>
      <figure className="ax-stage" aria-live="off">
        <div className="ax-stage-bar" aria-hidden>
          <i /><i /><i /><span>{cur.shotLabel}</span>
        </div>
        <div className="ax-stage-frame">
          <img key={cur.shot} src={`/screens/${cur.shot}.webp?v=2`} alt={`${cur.shotLabel} screen in NyumbaSuite`}
            width={1208} height={755} />
        </div>
        <figcaption>{cur.shotLabel}, as it looks in the product. Figures on the screens are illustrative.</figcaption>
      </figure>
      <div className="ax-pillar-list" role="tablist" aria-orientation="vertical" aria-label="What makes NyumbaSuite different">
        {items.map((p, n) => {
          const on = n === i;
          return (
            <div key={p.title} className={`ax-pillar ${on ? 'on' : ''}`}>
              <button type="button" role="tab" id={`pl-t-${n}`} aria-selected={on} aria-controls={`pl-p-${n}`}
                tabIndex={on ? 0 : -1} ref={(el) => { tabs.current[n] = el; }}
                onClick={() => pick(n)} onKeyDown={(e) => onKey(e, n)}>
                <span className={`mk-card-icon ic-${p.ic}`} aria-hidden>{icons[p.icon]}</span>
                <b>{p.title}</b>
                <small aria-hidden>{String(n + 1).padStart(2, '0')}</small>
              </button>
              <div className="ax-pillar-body" role="tabpanel" id={`pl-p-${n}`} aria-labelledby={`pl-t-${n}`} hidden={!on}>
                <p>{p.body}</p>
                {on && playing && <span className="ax-pillar-timer" key={`${n}-t`} style={{ ['--ms' as string]: `${PILLAR_MS}ms` }} aria-hidden />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The question finder, in place of the table                         */
/* ------------------------------------------------------------------ */

export function GlanceFinder({ facts }: { facts: { label: string; value: ReactNode }[] }) {
  const [i, setI] = useState(0);
  const [q, setQ] = useState('');
  const list = facts.filter((f) => f.label.toLowerCase().includes(q.trim().toLowerCase()));
  const cur = list.find((f) => f === facts[i]) ?? list[0];

  return (
    <div className="ax-glance">
      <label className="ax-search">
        <span className="sr-only">Search the questions</span>
        <input type="search" value={q} placeholder="Search, for example payments or tenants"
          onChange={(e) => setQ(e.target.value)} />
      </label>
      <div className="ax-glance-grid">
        <ul className="ax-glance-list" role="tablist" aria-orientation="vertical">
          {list.map((f) => (
            <li key={f.label}>
              <button type="button" role="tab" aria-selected={f === cur}
                className={`ax-q ${f === cur ? 'on' : ''}`} onClick={() => setI(facts.indexOf(f))}>
                {f.label}
              </button>
            </li>
          ))}
          {list.length === 0 && <li className="ax-empty">Nothing matches that. Try one word.</li>}
        </ul>
        <div className="ax-answer" role="tabpanel" key={cur?.label ?? 'none'}>
          {cur ? (<><h3>{cur.label}</h3><div>{cur.value}</div></>) : <p>Choose a question.</p>}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The roadmap track                                                  */
/* ------------------------------------------------------------------ */

export interface Stage { tone: string; stage: string; badge: string; title: string; items: string[] }

export function RoadmapTrack({ stages }: { stages: Stage[] }) {
  const [i, setI] = useState(0);
  const s = stages[i]!;
  return (
    <div className="ax-road">
      <div className="ax-road-bar" role="tablist" aria-label="Now, next and future">
        {stages.map((x, k) => (
          <button key={x.stage} type="button" role="tab" aria-selected={k === i}
            className={`ax-stop ${x.tone} ${k === i ? 'on' : ''} ${k < i ? 'past' : ''}`} onClick={() => setI(k)}>
            <span className="dot" aria-hidden />
            <b>{x.stage}</b>
            <small>{x.items.length} {x.tone === 'now' ? 'live' : x.tone === 'next' ? 'planned' : 'exploring'}</small>
          </button>
        ))}
      </div>
      <div className={`ax-road-body ${s.tone}`} role="tabpanel" key={s.stage}>
        <span className="ax-pill-road">{s.badge}</span>
        <h3>{s.title}</h3>
        <ul>
          {s.items.map((it, k) => <li key={it} style={{ ['--i' as string]: k }}>{it}</li>)}
        </ul>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* What we stand for                                                  */
/* ------------------------------------------------------------------ */

export function ValuesAccordion({ values, vision, mission }: {
  values: { accent: string; title: string; body: string }[]; vision: string; mission: string;
}) {
  const [open, setOpen] = useState(0);
  const [lens, setLens] = useState<'vision' | 'mission'>('vision');
  return (
    <div className="ax-values">
      <div className="ax-lens">
        <div className="ax-chips" role="group" aria-label="Vision or mission">
          <button type="button" className={`ax-chip ${lens === 'vision' ? 'on' : ''}`} aria-pressed={lens === 'vision'} onClick={() => setLens('vision')}>Our vision</button>
          <button type="button" className={`ax-chip ${lens === 'mission' ? 'on' : ''}`} aria-pressed={lens === 'mission'} onClick={() => setLens('mission')}>Our mission</button>
        </div>
        <p key={lens} className="ax-lens-text">{lens === 'vision' ? vision : mission}</p>
      </div>
      <div className="ax-acc">
        {values.map((v, k) => (
          <div key={v.title} className={`ax-acc-item ic-${v.accent} ${k === open ? 'on' : ''}`}>
            <button type="button" aria-expanded={k === open} onClick={() => setOpen(k)}>
              <span>{v.title}</span><i aria-hidden />
            </button>
            <div className="ax-acc-body"><p>{v.body}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}
